import react from "react";
import * as ui from "@material-ui/core";
import * as auth from "../../auth";
import * as rest from "../../rest";
import * as datetime from "../../datetime";
import { definitions } from "../../schema/api";
import { typeValueToLabel } from "../../const/report_image_type";
import { resources, controls } from "../../const/resource";
import { RecordList } from "../../components/record_list";

const labels = {
  // reportDetail: "日報詳細",
  notFound: "現在共有資料はありません",
  // download: "ダウンロード",
  // downloading: "ダウンロード中...",
  // maxDownloadExceededAlert: (count: number): string =>
  //   `一度にダウンロードできる画像の数は ${count}枚までです。`,
  name: "名前",
  type: "種類",
  thumbnail: "サムネイル",
  download: "ダウンロード",
  delete: "削除",
};

type ReportImageRow = {
  reportId: number;
  reportImageId: number;
  date: Date;
  chainName: string;
  storeName: string;
  staffName: string;
  chainId: number;
  storeId: number;
  instructionTitle: string;
  typeLabel: string;
  imagePath: string;
  imageAlt: string;
};

type RetrieveState = {
  initialized: boolean;
  reports: definitions["Report"][];
  reportImages: definitions["ReportImage"][];
  reportImageRows: ReportImageRow[];
  reportImageTypes: definitions["ReportImageType"][];
  instructions: definitions["Instruction"][];
  instructionId: number;
  staffs: definitions["Staff"][];
  filteredStaffs: definitions["Staff"][];
  staffIds: number[];
  chains: definitions["Chain"][];
  chainId: number;
  storeSearchWord: string;
  stores: definitions["Store"][];
  filteredStores: definitions["Store"][];
  storeIds: number[];
  area: string;
  type: number;
  photosPerPage: number;
  currentPage: number;
  dateChecked: boolean;
  staffNameChecked: boolean;
  storeNameChecked: boolean;
  preview: JSX.Element | null;
  downloading: boolean;
};

const anyType = 0;
const anyInstructionId = 0;
const anyStaffId = 0;
const anyChainId = 0;
const anyStoreId = 0;

const maxReportImageDownloadCount = 60;

function Retrieve() {
  const today = new Date();
  const [state, setState] = react.useState<RetrieveState>({
    initialized: false,
    reports: [],
    reportImages: [],
    reportImageRows: [],
    reportImageTypes: [],
    instructions: [],
    instructionId: anyInstructionId,
    staffs: [],
    filteredStaffs: [],
    staffIds: [anyStaffId],
    chains: [],
    chainId: anyChainId,
    storeSearchWord: "",
    stores: [],
    filteredStores: [],
    storeIds: [anyStoreId],
    area: "",
    type: anyType,
    photosPerPage: 4,
    dateChecked: true,
    currentPage: 1,
    staffNameChecked: true,
    storeNameChecked: true,
    preview: null,
    downloading: false,
  });

  const updateReportImageRows = () => {
    state.reportImageRows = state.reports
      .filter((report) => {
        if (
          state.staffIds.indexOf(anyStaffId) === -1 &&
          state.staffIds.indexOf(report.staff_id) === -1
        ) {
          return false;
        }
        if (state.chainId !== anyChainId && report.chain_id !== state.chainId) {
          return false;
        }
        if (
          state.storeIds.indexOf(anyStoreId) === -1 &&
          state.storeIds.indexOf(report.store_id || -1) === -1
        ) {
          return false;
        }
        return true;
      })
      .map((report: definitions["Report"]) => {
        const rows = report.report_images
          .filter((reportImage) => {
            if (state.type !== anyType && reportImage.type !== state.type) {
              return false;
            }
            if (
              state.instructionId !== anyInstructionId &&
              reportImage.instruction_id !== state.instructionId
            ) {
              return false;
            }
            return true;
          })
          .map((reportImage: definitions["ReportImage"]) => {
            const instruction = state.instructions.find(
              (instruction) => instruction.id === reportImage.instruction_id
            );

            return {
              reportId: report.id,
              reportImageId: reportImage.id,
              date: new Date(report.date),
              chainName: report.chain?.name || "",
              storeName: report.store?.name || "",
              staffName: report.staff?.name || "",
              chainId: report.chain?.id || 0,
              storeId: report.store?.id || 0,
              instructionTitle: instruction ? instruction.title : "",
              typeLabel: typeValueToLabel(reportImage.type),
              imagePath: `/${reportImage.path}`,
              imageAlt: reportImage.name,
            };
          });

        return rows;
      })
      .flat()
      .sort((l, r) => {
        if (l.date.getTime() === r.date.getTime()) {
          if (l.chainId === r.chainId) {
            if (l.storeId === r.storeId) {
              return 0;
            }
            return l.storeId < r.storeId ? -1 : 1;
          }
          return l.chainId < r.chainId ? -1 : 1;
        }
        return l.date.getTime() < r.date.getTime() ? -1 : 1;
      });
  };

  react.useEffect(() => {
    (async () => {
      if (state.initialized) {
        return;
      }
      state.initialized = true;

      const [reportImageTypes, staffs] = await Promise.all([
        (new rest.ReportImageType()).getAll(auth.getToken()),
        (new rest.Staff()).getAll(auth.getToken()),
      ]);
      state.reportImageTypes = reportImageTypes;
      state.staffs = staffs.filter((staff) => !staff.is_quitted);
      state.filteredStaffs = state.staffs;

      updateReportImageRows();
      setState({ ...state });
    })();
  });

  const showPreview = (path: string, alt: string) => {
    state.preview = (
      <ui.Box>
        <img src={path} width="100%" alt={alt} />
      </ui.Box>
    );
    setState({ ...state });
  };

  const onPageChanged = (page: number) => {
    state.currentPage = page;
    setState({ ...state });
  };

  return (
    <ui.Container>
      <ui.Grid container spacing={4}>
        <ui.Grid item xs={12}></ui.Grid>

        <ui.Grid item xs={12}>
          <RecordList<ReportImageRow>
            defaultText={labels.notFound}
            cols={[
              { label: labels.name, key: "name" },
              { label: labels.type, key: "type" },
            ]}
            sort={false}
            items={state.reportImageRows}
            itemsPerPage={maxReportImageDownloadCount}
            perPageCandidates={[maxReportImageDownloadCount]}
            onPageChanged={onPageChanged}
            extraKeyItemCallbacks={{
              date: (row) => (
                <>{datetime.toTextInputCalendarFormat(row.date)}</>
              ),
              // control: (row) => (
              //   <ui.Grid container spacing={2}>
              //     <ui.Grid container item xs={6} justifyContent="center">
              //       <ui.Button
              //         color="secondary"
              //         onClick={() => showPreview(row.imagePath, row.imageAlt)}
              //       >
              //         {labels.preview}
              //       </ui.Button>
              //     </ui.Grid>
              //     <ui.Grid
              //       container
              //       item
              //       xs={6}
              //       justifyContent="center"
              //       alignItems="center"
              //     >
              //       <ui.Link
              //         href={`/${resources.report.identifier}/${controls.update.identifier}/${row.reportId}`}
              //       >
              //         {labels.reportDetail}
              //       </ui.Link>
              //     </ui.Grid>
              //   </ui.Grid>
              // ),
            }}
          />
        </ui.Grid>
      </ui.Grid>
    </ui.Container>
  );
}

export { Retrieve };
